import { createLogger, PLAYER_LOG_EVENTS, logPlayerEvent } from "../helpers/logger.js";
import { getSafeSiteUrl, getTopPageUrl } from "./top-page.js";

/**
 * Παίρνει το επόμενο URL ανά τύπο διαφήμισης
 */
export function getNextAdUrl(type, state) {
	let url = null;

	if (type === "preroll") {
		if (state.prerollIndex < state.prerollUrls.length) {
			url = state.prerollUrls[state.prerollIndex];
			state.prerollIndex++;
		}
	} else if (type === "fallback") {
		if (state.fallbackIndex < state.fallbackUrls.length) {
			url = state.fallbackUrls[state.fallbackIndex];
			state.fallbackIndex++;
		}
	} else if (type === "midroll") {
		if (state.midrollIndex < state.midrollUrls.length) {
			url = state.midrollUrls[state.midrollIndex];
			state.midrollIndex++;
		}
	} else if (type === "postroll") {
		if (state.postrollIndex < state.postrollUrls.length) {
			url = state.postrollUrls[state.postrollIndex];
			state.postrollIndex++;
		}
	}
	return url;
}

/**
 * Παίζει το επόμενο ad
 */
export function playNextAd(player, type, state, first = false, debug = false) {
  const log = createLogger("AdsManager", debug);
  const adUrl = getNextAdUrl(type, state);
  if (!adUrl) {
    log.debug("ℹ No ad URL for type:", type);
    return false;
  }

  if (!player || player.__destroying || player.isDisposed?.() || !player.el?.()) {
    log.debug("Player not available for ad request");
    return false;
  }

  if (
    !player.ima ||
    typeof player.ima.changeAdTag !== "function" ||
    typeof player.ima.requestAds !== "function"
  ) {
    log.debug("IMA API not ready");
    return false;
  }

  if (!state.adsOptional) {
    try { player.pause(); } catch {}
  }

  if (first && typeof player.ima.initializeAdDisplayContainer === "function") {
    try { player.ima.initializeAdDisplayContainer(); } catch {}
  }

  try {
    log.debug("🎯 Requesting ad tag:", adUrl);

    player.ima.changeAdTag(adUrl);

    log.debug("🎯 FINAL adUrl", adUrl);

    logPlayerEvent(log, PLAYER_LOG_EVENTS.REQUEST_SENT, {
      type,
      url: adUrl,
    });

    player.ima.requestAds();
    return true;
  } catch (e) {
    log.debug("IMA ad request failed", e);
    return false;
  }
}


function getUrlParseBase() {
  try {
    return window.location.href || "https://video.cleon.tv/";
  } catch {
    return "https://video.cleon.tv/";
  }
}

function checkVast(
  vastUrl,
  consent = {},
  timeout = 7000,
  debug = false
) {
  const log = createLogger("AdsManager", debug);

  return new Promise((resolve) => {
    if (!vastUrl) return resolve(false);

    try {
      const u = new URL(String(vastUrl || "").trim(), getUrlParseBase());

      const gpp = consent?.gppString || "";
      const gppSid = consent?.gppSid || "";
      const gdprConsent = consent?.tcString || consent?.gdprString || "";
      const usPrivacy = consent?.uspString || "";

      const gdpr =
        consent?.gdprApplies === true
          ? "1"
          : consent?.gdprApplies === false
            ? "0"
            : "";

      if (gdpr !== "") {
        u.searchParams.set("gdpr", gdpr);
      }

      if (gdprConsent) {
        u.searchParams.set("gdpr_consent", gdprConsent);
      }

      if (gpp) {
        u.searchParams.set("gpp", gpp);
      }

      if (gppSid) {
        u.searchParams.set("gpp_sid", gppSid);
      }

      if (usPrivacy) {
        u.searchParams.set("us_privacy", usPrivacy);
      }

      const siteUrl = getSafeSiteUrl(
        getTopPageUrl() || document.referrer || window.location.href
      );

      if (siteUrl) {
        u.searchParams.set("siteurl", siteUrl);
      }

      vastUrl = u.toString();
    } catch (e) {
      log.debug("Invalid VAST URL", {
        url: vastUrl,
        message: e?.message || String(e),
      });
    }

    const proxyUrl =
      "https://video.cleon.tv/api/vast-check?url=" +
      encodeURIComponent(vastUrl);

    const xhr = new XMLHttpRequest();
    xhr.open("GET", proxyUrl, true);
    xhr.timeout = timeout;
    xhr.withCredentials = false;

    xhr.onload = () => {
      if (xhr.status !== 200) return resolve(false);

      try {
        const data = JSON.parse(xhr.responseText || "{}");
        resolve(data.ok === true);
      } catch {
        resolve(false);
      }
    };

    xhr.onerror = () => resolve(false);

    xhr.ontimeout = () => {
      logPlayerEvent(log, PLAYER_LOG_EVENTS.TIMEOUT, {
        url: vastUrl,
        timeoutMs: timeout,
      });

      resolve(false);
    };

    xhr.send();
  });
}

export async function filterPlayablePrerolls(prerollUrls = [], consent = {}, debug = false) {
  const log = createLogger("AdsManager", debug);

  const checks = prerollUrls
    .filter(Boolean)
    .map(async (url) => {
      try {
        const ok = await checkVast(url, consent, 8000, debug);
        if (!ok) {
          log.debug("❌ Invalid VAST skipped:", url);
          return null;
        }
        return url;
      } catch (e) {
        log.debug("❌ VAST check failed:", url, e);
        return null;
      }
    });

  const results = await Promise.all(checks);
  return results.filter(Boolean);
}